import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";

/**
 * The button.
 *
 * Four variants, and no more. A data application needs a clear primary action, a
 * neutral default, a quiet action for row-level use, and a destructive one — every
 * additional variant makes the hierarchy less legible, not more.
 *
 * Heights line up with the input control heights so a filter bar composes without
 * per-component nudging.
 */

export type KnopfVariante = "primaer" | "standard" | "leise" | "gefahr";
export type KnopfGroesse = "klein" | "normal";

export interface KnopfProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: KnopfVariante;
  groesse?: KnopfGroesse;
  /** Renders a leading icon slot at the correct optical size. */
  icon?: ReactNode;
  /** Shows a busy state and blocks repeat submits. */
  laeuft?: boolean;
}

const basis =
  "inline-flex items-center justify-center gap-[6px] whitespace-nowrap select-none " +
  "border font-[var(--weight-medium)] " +
  "transition-[background-color,border-color,color] duration-[var(--dur-1)] ease-[var(--ease-standard)] " +
  "disabled:opacity-55 disabled:cursor-not-allowed";

const groessen: Record<KnopfGroesse, string> = {
  klein: "h-[24px] px-[8px] text-[length:var(--text-xs)] rounded-[var(--radius-2)]",
  normal: "h-[30px] px-[12px] text-[length:var(--text-sm)] rounded-[var(--radius-2)]",
};

const varianten: Record<KnopfVariante, string> = {
  primaer:
    "bg-[var(--accent-solid)] border-[var(--accent-solid)] text-[var(--accent-on-solid)] " +
    "hover:bg-[var(--accent-solid-hover)] hover:border-[var(--accent-solid-hover)]",
  standard:
    "bg-[var(--surface-raised)] border-[var(--line-field)] text-[var(--text-primary)] " +
    "hover:bg-[var(--surface-sunken)]",
  leise:
    "bg-transparent border-transparent text-[var(--text-secondary)] " +
    "hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]",
  gefahr:
    "bg-[var(--surface-raised)] border-[var(--line-field)] text-[var(--status-bad-tx)] " +
    "hover:bg-[var(--status-bad-bg)] hover:border-[var(--status-bad-bar)]",
};

export const Knopf = forwardRef<HTMLButtonElement, KnopfProps>(function Knopf(
  { variante = "standard", groesse = "normal", icon, laeuft = false, disabled, children, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      disabled={disabled || laeuft}
      aria-busy={laeuft || undefined}
      className={clsx(basis, groessen[groesse], varianten[variante], className)}
      {...rest}
    >
      {laeuft ? <Spinner /> : icon}
      {children}
    </button>
  );
});

/**
 * A two-frame rotation rather than a spinner illustration. At 12 px anything more
 * detailed is mush, and the point is only to say "still working".
 */
function Spinner() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      className="motion-safe:animate-spin"
    >
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" opacity="0.25" />
      <path d="M11 6a5 5 0 0 0-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
